import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlantusLogo } from "./Logo";
import { NAV } from "@/constants/plantusTestIds";
import { List, X, SignIn } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const navItems = [
  { label: "Funktionen", href: "#features", testId: NAV.linkFeatures },
  { label: "KI", href: "#ki", testId: "nav-link-ki" },
  { label: "Demo", href: "#demo", testId: NAV.linkDemo },
  { label: "Preise", href: "/preise", testId: "nav-link-pricing", route: true },
  { label: "FAQ", href: "#faq", testId: NAV.linkFaq },
  { label: "Kontakt", href: "#contact", testId: NAV.linkContact },
];

export const Navbar = () => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-bone/80 border-b border-bone-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid={NAV.logo}>
          <PlantusLogo size="lg" />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((item) =>
            item.route ? (
              <Link
                key={item.href}
                to={item.href}
                data-testid={item.testId}
                className="font-body text-sm font-medium text-forest-700/80 hover:text-forest-700 transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.href}
                href={item.href}
                data-testid={item.testId}
                className="font-body text-sm font-medium text-forest-700/80 hover:text-forest-700 transition-colors"
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <Link
              to="/app"
              data-testid="nav-go-app"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-4 sm:px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              Web-App öffnen
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-forest-700 hover:text-forest-900 px-3 py-2 rounded-full transition-colors"
              >
                <SignIn size={16} weight="bold" />
                <span className="hidden sm:inline">Anmelden</span>
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-4 sm:px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                Kostenlos starten
              </Link>
            </>
          )}
          <button
            type="button"
            aria-label="Menü"
            className="md:hidden p-2 text-forest-700"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-bone-200/70 bg-bone/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navItems.map((item) =>
              item.route ? (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className="font-body text-base font-medium text-forest-700/90 py-1"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="font-body text-base font-medium text-forest-700/90 py-1"
                >
                  {item.label}
                </a>
              )
            )}
            <div className="pt-3 border-t border-bone-200 flex flex-col gap-2">
              {user ? (
                <Link
                  to="/app"
                  onClick={() => setOpen(false)}
                  className="inline-flex justify-center bg-forest-600 text-white font-medium text-sm px-5 py-3 rounded-full"
                >
                  Web-App öffnen
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-forest-700 px-5 py-3 rounded-full border border-forest-600/30"
                  >
                    <SignIn size={16} weight="bold" /> Anmelden
                  </Link>
                  <a
                    href="#download"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-forest-700 px-5 py-3 rounded-full border border-forest-600/30"
                  >
                    App herunterladen
                  </a>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="inline-flex justify-center bg-forest-600 text-white font-medium text-sm px-5 py-3 rounded-full"
                  >
                    Kostenlos starten
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
